<template>
  <div class="admin-settings">
    <h2>{{ $t('legacy.Настройки') }}</h2>
    
    <div class="settings-section">
      <h3>{{ $t('legacy.Тарифы подписки') }}</h3>
      <p class="section-description">{{ $t('Настройка цен и кредитов для тарифных планов') }}</p>
      
      <div v-if="subscriptionsLoading" class="loading">{{ $t('common.loading') }}</div>
      
      <div v-else class="packages-list">
        <div v-for="sub in subscriptions" :key="sub.id" class="package-card subscription-card">
          <div class="package-info">
            <span class="package-name">{{ sub.name }}</span>
            <span class="package-credits">{{ sub.plan_id === 'base' ? $t('subscription.base') : $t('subscription.premium') }} {{ $t('Тариф') }}</span>
          </div>
          <div class="package-controls">
            <div class="price-input-group">
              <label>{{ $t('legacy.Цена') }} (₽):</label>
              <input 
                type="number" 
                v-model.number="sub.price" 
                min="0" 
                step="100"
                class="price-input"
                @change="markSubscriptionChanged(sub.id)"
              />
            </div>
            <div class="price-input-group">
              <label :for="`subscription-usd-${sub.id}`">{{ $t('admin.usdPrice') }}:</label>
              <input :id="`subscription-usd-${sub.id}`" v-model.trim="sub.price_usd"
                type="number" min="0.01" max="99999999" step="0.01" placeholder="—"
                class="price-input" @input="markSubscriptionChanged(sub.id)" />
            </div>
            <div class="price-input-group">
              <label>{{ $t('AI-кредитов') }}:</label>
              <input 
                type="number" 
                v-model.number="sub.ai_credits" 
                min="0" 
                step="1"
                class="price-input credits-input"
                @change="markSubscriptionChanged(sub.id)"
              />
            </div>
            <div class="active-toggle">
              <label class="toggle-label">
                <input type="checkbox" v-model="sub.is_active" @change="markSubscriptionChanged(sub.id)" />
                <span>{{ $t('legacy.Активен') }}</span>
              </label>
            </div>
            <button 
              v-if="changedSubscriptions.includes(sub.id)"
              class="btn-save"
              @click="saveSubscription(sub)"
              :disabled="savingSubscription === sub.id"
            >
              {{ savingSubscription === sub.id ? $t('legacy.Сохранение...') : $t('common.save') }}
            </button>
          </div>
        </div>
      </div>
      
      <p class="field-hint">{{ $t('admin.usdPriceHint') }}</p>
      <div v-if="subscriptionError" class="error-message">{{ subscriptionError }}</div>
    </div>
    
    <div class="settings-section">
      <h3>{{ $t('legacy.Пакеты кредитов') }}</h3>
      <p class="section-description">{{ $t('Настройка цен на пакеты кредитов для ИИ обработки') }}</p>
      
      <div v-if="loading" class="loading">{{ $t('common.loading') }}</div>
      
      <div v-else class="packages-list">
        <div v-for="pkg in packages" :key="pkg.id" class="package-card">
          <div class="package-info">
            <span class="package-name">{{ pkg.name }}</span>
          </div>
          <div class="package-controls">
            <div class="price-input-group">
              <label>{{ $t('Кредиты') }}:</label>
              <input
                type="number"
                v-model.number="pkg.credits"
                min="1"
                step="1"
                class="price-input credits-input"
                @change="markChanged(pkg.id)"
              />
            </div>
            <div class="price-input-group">
              <label>{{ $t('legacy.Цена') }} (₽):</label>
              <input 
                type="number" 
                v-model.number="pkg.price" 
                min="0" 
                step="50"
                class="price-input"
                @change="markChanged(pkg.id)"
              />
            </div>
            <div class="price-input-group">
              <label :for="`package-usd-${pkg.id}`">{{ $t('admin.usdPrice') }}:</label>
              <input :id="`package-usd-${pkg.id}`" v-model.trim="pkg.price_usd"
                type="number" min="0.01" max="99999999" step="0.01" placeholder="—"
                class="price-input" @input="markChanged(pkg.id)" />
            </div>
            <div class="active-toggle">
              <label class="toggle-label">
                <input type="checkbox" v-model="pkg.is_active" @change="markChanged(pkg.id)" />
                <span>{{ $t('legacy.Активен') }}</span>
              </label>
            </div>
            <button 
              v-if="changedPackages.includes(pkg.id)"
              class="btn-save"
              @click="savePackage(pkg)"
              :disabled="saving === pkg.id"
            >
              {{ saving === pkg.id ? $t('legacy.Сохранение...') : $t('common.save') }}
            </button>
          </div>
        </div>
      </div>
      
      <div v-if="error" class="error-message">{{ error }}</div>
    </div>
    
    <div class="settings-section">
      <h3>{{ $t('legacy.Управление кэшем') }}</h3>
      <p class="section-description">{{ $t('Очистка кэша приложения для всех пользователей') }}</p>
      
      <div class="cache-controls">
        <button 
          class="btn-clear-cache"
          @click="clearCache"
          :disabled="clearingCache"
        >
          {{ clearingCache ? $t('legacy.Очистка...') : $t('legacy.Очистить кэш') }}
        </button>
        <p class="cache-hint">{{ $t('Это обновит версию кэша и заставит клиентов загрузить свежие данные') }}</p>
      </div>
      
      <div v-if="cacheSuccess" class="success-message">{{ cacheSuccess }}</div>
    </div>

    <div class="settings-section">
      <h3>{{ $t('legacy.Настройки ЮКасса') }}</h3>
      <p class="section-description">{{ $t('Настройка интернет-эквайринга для приёма платежей') }}</p>
      
      <div v-if="paymentLoading" class="loading">{{ $t('common.loading') }}</div>
      
      <div v-else class="payment-settings-form">
        <div class="form-group">
          <label for="shopId">{{ $t('Shop ID (Идентификатор магазина)') }}:</label>
          <input 
            type="text" 
            id="shopId"
            v-model="paymentSettings.shopId" 
            :placeholder="$t('legacy.Введите Shop ID')"
            class="form-input"
          />
        </div>
        
        <div class="form-group">
          <label for="secretKey">{{ $t('Secret Key (Секретный ключ)') }}:</label>
          <input 
            type="text" 
            id="secretKey"
            v-model="paymentSettings.secretKey" 
            :placeholder="$t('legacy.Введите Secret Key')"
            class="form-input"
          />
          <p class="field-hint" v-if="paymentSettings.secretKey">{{ $t('Ключ сохранён в базе данных.') }}</p>
          <p class="field-hint" v-else>{{ $t('Ключ не установлен.') }}</p>
        </div>
        
        <div class="form-group">
          <label class="toggle-label">
            <input type="checkbox" v-model="paymentSettings.isActive" />
            <span>{{ $t('legacy.Платежи активны') }}</span>
          </label>
        </div>

        <div class="form-group">
          <label class="toggle-label">
            <input type="checkbox" v-model="paymentSettings.enableRecurringPayments" />
            <span>{{ $t('Автопродление (рекуррентные платежи)') }}</span>
          </label>
          <p class="field-hint">
            {{ $t('Включите только если в личном кабинете ЮКасса активированы рекуррентные платежи (договор автоплатежей). При включении при первой оплате карта сохраняется и подписка продлевается автоматически.') }}
          </p>
        </div>
        
        <div class="form-actions">
          <button 
            class="btn-save"
            @click="savePaymentSettings"
            :disabled="savingPayment"
          >
            {{ savingPayment ? $t('legacy.Сохранение...') : $t('legacy.Сохранить настройки') }}
          </button>
        </div>
        
        <div v-if="paymentSuccess" class="success-message">{{ paymentSuccess }}</div>
        <div v-if="paymentError" class="error-message">{{ paymentError }}</div>
        
        <div class="webhook-info">
          <h4>{{ $t('legacy.Настройка Webhook в ЮКасса') }}</h4>
          <p>{{ $t('Для автоматической активации подписок после оплаты добавьте webhook в личном кабинете ЮКасса:') }}</p>
          <code class="webhook-url">https://delper.pro/api/payments/webhook</code>
          <p class="webhook-hint">{{ $t('Выберите событие') }}: <strong>payment.succeeded</strong></p>
        </div>
      </div>
    </div>

    <div class="settings-section">
      <h3>{{ $t('admin.prodamusTitle') }}</h3>
      <p class="section-description">{{ $t('admin.prodamusDescription') }}</p>
      <div v-if="prodamusLoading" class="loading">{{ $t('common.loading') }}</div>
      <div v-else class="payment-settings-form">
        <p v-if="prodamusSettings.source === 'env'" class="field-hint">{{ $t('admin.prodamusEnvSource') }}</p>
        <div class="form-group">
          <label for="prodamusPageUrl">{{ $t('admin.prodamusPageUrl') }}</label>
          <input id="prodamusPageUrl" v-model.trim="prodamusSettings.pageUrl" type="url"
            placeholder="https://your-page.payform.ru/" class="form-input" />
        </div>
        <div class="form-group">
          <label for="prodamusSecret">{{ $t('admin.prodamusSecret') }}</label>
          <input id="prodamusSecret" v-model="prodamusSecret" type="password"
            autocomplete="new-password" :placeholder="$t('admin.prodamusSecretPlaceholder')" class="form-input" />
          <p class="field-hint">{{ prodamusSettings.hasSecretKey
            ? $t('admin.prodamusSecretSaved') : $t('admin.prodamusSecretMissing') }}</p>
          <p v-if="prodamusSettings.keySource === 'env'" class="field-hint">{{ $t('admin.prodamusEnvKey') }}</p>
        </div>
        <div class="form-group">
          <label for="prodamusWebhook">{{ $t('admin.prodamusWebhook') }}</label>
          <input id="prodamusWebhook" v-model.trim="prodamusSettings.webhookUrl" type="url"
            placeholder="https://your-published-domain/api/payments/prodamus/webhook" class="form-input" />
          <p class="field-hint">{{ $t('admin.prodamusWebhookHint') }}</p>
        </div>
        <div class="webhook-info">
          <h4>{{ $t('admin.prodamusWebhookCopyTitle') }}</h4>
          <template v-if="savedProdamusWebhook">
            <code class="webhook-url">{{ savedProdamusWebhook }}</code>
            <button type="button" class="btn-secondary" @click="copyProdamusWebhook">{{ $t('admin.prodamusCopy') }}</button>
          </template>
          <p v-else class="field-hint">{{ $t('admin.prodamusNoWebhook') }}</p>
          <p class="field-hint">{{ $t('admin.prodamusWebhookSysHint') }}</p>
        </div>
        <div class="form-group">
          <label for="prodamusSys">{{ $t('admin.prodamusSys') }}</label>
          <input id="prodamusSys" v-model.trim="prodamusSettings.sys" type="text" class="form-input" />
        </div>
        <div class="form-group">
          <label for="prodamusCurrency">{{ $t('admin.prodamusCurrency') }}</label>
          <select id="prodamusCurrency" v-model="prodamusSettings.currency"
            class="form-input" @change="onProdamusCurrencyChange">
            <option v-for="currency in ['RUB', 'USD', 'EUR', 'KZT']" :key="currency" :value="currency">{{ currency }}</option>
          </select>
          <p class="field-hint">{{ $t('admin.prodamusUsdOnly') }}</p>
        </div>
        <div v-if="prodamusSettings.currency !== 'RUB'">
          <h4>{{ $t('admin.prodamusPrices') }} ({{ prodamusSettings.currency }})</h4>
          <div v-for="id in prodamusSettings.catalog" :key="id" class="form-group">
            <label :for="`prodamus-price-${id}`">{{ prodamusProductLabel(id) }} ({{ id }})</label>
            <input v-if="prodamusSettings.currency === 'USD'" :id="`prodamus-price-${id}`"
              :value="prodamusSettings.usdPrices?.[id] || ''" type="text"
              readonly :placeholder="$t('admin.usdPriceMissing')" class="form-input" />
            <input v-else :id="`prodamus-price-${id}`" v-model.trim="prodamusSettings.priceMap[id]"
              type="text" inputmode="decimal" placeholder="0.00" class="form-input" />
          </div>
          <p class="field-hint">{{ prodamusSettings.currency === 'USD'
            ? $t('admin.prodamusSharedUsdHint') : $t('admin.prodamusPriceHint') }}</p>
        </div>
        <div class="form-group">
          <label class="toggle-label">
            <input v-model="prodamusSettings.worldPaymentsEnabled" type="checkbox" />
            <span>{{ $t('admin.prodamusWorldConfirmed') }}</span>
          </label>
        </div>
        <div class="form-group">
          <label class="toggle-label">
            <input v-model="prodamusSettings.webhookReady" type="checkbox" />
            <span>{{ $t('admin.prodamusWebhookConfirmed') }}</span>
          </label>
        </div>
        <div class="form-group">
          <label class="toggle-label">
            <input v-model="prodamusSettings.isActive" type="checkbox" />
            <span>{{ $t('admin.prodamusActive') }}</span>
          </label>
          <p class="field-hint">{{ $t('admin.prodamusActivationHint') }}</p>
        </div>
        <div class="form-actions">
          <button type="button" class="btn-save" :disabled="savingProdamus" @click="saveProdamus">
            {{ savingProdamus ? $t('legacy.Сохранение...') : $t('legacy.Сохранить настройки') }}
          </button>
        </div>
        <p v-if="prodamusSettings.available" class="success-message">{{ $t('admin.prodamusAvailable') }}</p>
        <p v-else class="field-hint">{{ $t('admin.prodamusUnavailable') }}</p>
        <p v-if="prodamusSuccess" class="success-message">{{ prodamusSuccess }}</p>
        <p v-if="prodamusError" class="error-message">{{ prodamusError }}</p>
      </div>
    </div>

    <div class="settings-section">
      <h3>{{ $t('legacy.Настройки Email') }}</h3>
          <p class="section-description">{{ $t('Настройка SMTP-сервера для отправки писем (восстановление пароля, приветствие)') }}</p>

      <div v-if="smtpLoading" class="loading">{{ $t('common.loading') }}</div>

      <div v-else class="payment-settings-form">
        <div v-if="smtpSettings.source === 'env'" class="info-message">
          {{ $t('legacy.Настройки определены из переменных окружения. Для управления через панель активируйте и сохраните.') }}
        </div>

        <div class="form-group">
          <label for="smtpHost">SMTP Host:</label>
          <input
            type="text"
            id="smtpHost"
            v-model="smtpSettings.host"
            placeholder="smtp.example.com"
            class="form-input"
          />
        </div>

        <div class="form-group">
          <label for="smtpPort">Port:</label>
          <input
            type="number"
            id="smtpPort"
            v-model.number="smtpSettings.port"
            placeholder="465"
            class="form-input"
          />
          <p class="field-hint">465 — SSL (secure), 587 — STARTTLS</p>
        </div>

        <div class="form-group">
          <label for="smtpUser">{{ $t('Пользователь (логин)') }}:</label>
          <input
            type="text"
            id="smtpUser"
            v-model="smtpSettings.username"
            placeholder="no-reply@maketta.pro"
            class="form-input"
          />
        </div>

        <div class="form-group">
          <label for="smtpPass">{{ $t('Пароль') }}:</label>
          <div class="input-with-toggle">
            <input
              :type="showSmtpPassword ? 'text' : 'password'"
              id="smtpPass"
              v-model="smtpSettings.password"
              :placeholder="$t('legacy.Введите пароль')"
              class="form-input"
            />
            <button
              type="button"
              class="toggle-password-btn"
              @click="showSmtpPassword = !showSmtpPassword"
              :title="showSmtpPassword ? $t('legacy.Скрыть') : $t('legacy.Показать')"
            >{{ showSmtpPassword ? '🙈' : '👁' }}</button>
          </div>
        </div>

        <div class="form-group">
          <label for="smtpFrom">{{ $t('Адрес отправителя (From)') }}:</label>
          <input
            type="text"
            id="smtpFrom"
            v-model="smtpSettings.fromEmail"
            placeholder="no-reply@maketta.pro"
            class="form-input"
          />
        </div>

        <div class="form-group">
          <label for="smtpAppUrl">{{ $t('URL сайта (для ссылок в письмах)') }}:</label>
          <input
            type="text"
            id="smtpAppUrl"
            v-model="smtpSettings.appUrl"
            placeholder="https://maketta.pro"
            class="form-input"
          />
          <p class="field-hint">{{ $t('Используется в ссылках «Войти» и «Сбросить пароль»') }}</p>
        </div>

        <div class="form-group">
          <label class="toggle-label">
            <input type="checkbox" v-model="smtpSettings.isActive" />
            <span>{{ $t('Настройки активны (используются вместо env-переменных)') }}</span>
          </label>
        </div>

        <div class="form-actions">
          <button
            class="btn-save"
            @click="saveSmtpSettings"
            :disabled="savingSmtp"
          >
            {{ savingSmtp ? $t('legacy.Сохранение...') : $t('legacy.Сохранить настройки') }}
          </button>
          <button
            class="btn-secondary"
            @click="testSmtpConnection"
            :disabled="testingSmtp"
            style="margin-left: 12px;"
          >
            {{ testingSmtp ? $t('legacy.Проверка...') : $t('legacy.Проверить соединение') }}
          </button>
        </div>

        <div v-if="smtpSuccess" class="success-message">{{ smtpSuccess }}</div>
        <div v-if="smtpError" class="error-message">{{ smtpError }}</div>
        <div v-if="smtpTestResult" :class="smtpTestResult.success ? 'success-message' : 'error-message'">
          {{ smtpTestResult.message }}
        </div>
      </div>
    </div>

    <div class="settings-section">
      <h3>{{ $t('SEO и Open Graph') }}</h3>
      <p class="section-description">{{ $t('Настройка метатегов и превью для социальных сетей') }}</p>
      
      <div v-if="seoLoading" class="loading">{{ $t('common.loading') }}</div>
      
      <div v-else class="seo-settings-form">
        <h4>{{ $t('legacy.Метатеги') }}</h4>
        <div class="form-group">
          <label for="metaTitle">{{ $t('Title (заголовок страницы)') }}:</label>
          <input 
            type="text" 
            id="metaTitle"
            v-model="seoSettings.meta_title" 
            :placeholder="$t('Макетта — Онлайн-конструктор для event-декораторов')"
            class="form-input"
          />
        </div>
        
        <div class="form-group">
          <label for="metaDescription">{{ $t('Description (описание)') }}:</label>
          <textarea 
            id="metaDescription"
            v-model="seoSettings.meta_description" 
            :placeholder="$t('Создавайте фотозоны, рассчитывайте стоимость и экспортируйте проекты в PDF')"
            class="form-input form-textarea"
            rows="3"
          ></textarea>
        </div>
        
        <div class="form-group">
          <label for="metaKeywords">{{ $t('Keywords (ключевые слова)') }}:</label>
          <input 
            type="text" 
            id="metaKeywords"
            v-model="seoSettings.meta_keywords" 
            :placeholder="$t('фотозона, декор, event, конструктор')"
            class="form-input"
          />
        </div>
        
        <h4>{{ $t('Open Graph (Facebook, VK)') }}</h4>
        <div class="form-group">
          <label for="ogTitle">og:title:</label>
          <input 
            type="text" 
            id="ogTitle"
            v-model="seoSettings.og_title" 
            :placeholder="$t('Макетта — Конструктор фотозон')"
            class="form-input"
          />
        </div>
        
        <div class="form-group">
          <label for="ogDescription">og:description:</label>
          <textarea 
            id="ogDescription"
            v-model="seoSettings.og_description" 
            :placeholder="$t('Профессиональный инструмент для создания фотозон')"
            class="form-input form-textarea"
            rows="2"
          ></textarea>
        </div>
        
        <div class="form-group">
          <label for="ogSiteName">og:site_name:</label>
          <input 
            type="text" 
            id="ogSiteName"
            v-model="seoSettings.og_site_name" 
            :placeholder="$t('legacy.Макетта')"
            class="form-input"
          />
        </div>
        
        <div class="form-group">
          <label>{{ $t('og:image (рекомендуется 1200x630)') }}:</label>
          <div class="og-image-upload">
            <div v-if="seoSettings.og_image" class="og-image-preview">
              <img :src="seoSettings.og_image" alt="OG Image" />
              <button class="btn-remove-image" @click="removeOgImage">{{ $t('common.delete') }}</button>
            </div>
            <div v-else class="og-image-placeholder">
              <input 
                type="file" 
                ref="ogImageInput"
                accept="image/jpeg,image/png,image/webp"
                @change="handleOgImageUpload"
                style="display: none"
              />
              <button class="btn-upload" @click="$refs.ogImageInput.click()" :disabled="uploadingOgImage">
                {{ uploadingOgImage ? $t('common.loading') : $t('legacy.Загрузить изображение') }}
              </button>
            </div>
          </div>
        </div>
        
        <h4>{{ $t('Twitter Cards') }}</h4>
        <div class="form-group">
          <label for="twitterCard">twitter:card:</label>
          <select id="twitterCard" v-model="seoSettings.twitter_card" class="form-input">
            <option value="summary">summary</option>
            <option value="summary_large_image">summary_large_image</option>
          </select>
        </div>
        
        <div class="form-group">
          <label for="twitterTitle">twitter:title:</label>
          <input 
            type="text" 
            id="twitterTitle"
            v-model="seoSettings.twitter_title" 
            :placeholder="$t('Макетта — Конструктор фотозон')"
            class="form-input"
          />
        </div>
        
        <div class="form-group">
          <label for="twitterDescription">twitter:description:</label>
          <textarea 
            id="twitterDescription"
            v-model="seoSettings.twitter_description" 
            :placeholder="$t('Профессиональный инструмент для создания фотозон')"
            class="form-input form-textarea"
            rows="2"
          ></textarea>
        </div>
        
        <div class="form-actions">
          <button 
            class="btn-save"
            @click="saveSeoSettings"
            :disabled="savingSeo"
          >
            {{ savingSeo ? $t('legacy.Сохранение...') : $t('legacy.Сохранить SEO настройки') }}
          </button>
        </div>
        
        <div v-if="seoSuccess" class="success-message">{{ seoSuccess }}</div>
        <div v-if="seoError" class="error-message">{{ seoError }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from '@/api/axios';
import { getPaymentSettings, savePaymentSettings as savePaymentSettingsApi,
  getProdamusSettings, saveProdamusSettings } from '@/api/payments';
import { getHomepageSettings, updateHomepageSettings, uploadOgImage } from '@/api/siteSettings';
import { smtpApi } from '@/api/smtp';

export default {
  name: 'AdminSettings',
  data() {
    return {
      packages: [],
      subscriptions: [],
      loading: true,
      subscriptionsLoading: true,
      saving: null,
      savingSubscription: null,
      error: null,
      subscriptionError: null,
      changedPackages: [],
      changedSubscriptions: [],
      clearingCache: false,
      cacheSuccess: null,
      paymentLoading: true,
      savingPayment: false,
      paymentSuccess: null,
      paymentError: null,
      paymentSettings: {
        shopId: '',
        secretKey: '',
        hasSecretKey: false,
        isActive: false,
        enableRecurringPayments: false,
      },
      prodamusLoading: true,
      savingProdamus: false,
      prodamusError: null,
      prodamusSuccess: null,
      prodamusSecret: '',
      savedProdamusWebhook: '',
      prodamusSettings: {
        pageUrl: '', webhookUrl: '', currency: 'RUB', sys: '', priceMap: {},
        isActive: false, worldPaymentsEnabled: false, webhookReady: false,
        hasSecretKey: false, source: 'env', catalog: [], available: false,
      },
      seoLoading: true,
      savingSeo: false,
      seoSuccess: null,
      seoError: null,
      uploadingOgImage: false,
      seoSettings: {
        meta_title: '',
        meta_description: '',
        meta_keywords: '',
        og_title: '',
        og_description: '',
        og_image: '',
        og_site_name: '',
        twitter_card: 'summary_large_image',
        twitter_title: '',
        twitter_description: ''
      },
      smtpLoading: true,
      savingSmtp: false,
      testingSmtp: false,
      smtpSuccess: null,
      smtpError: null,
      smtpTestResult: null,
      showSmtpPassword: false,
      smtpSettings: {
        host: '',
        port: 465,
        username: '',
        password: '',
        fromEmail: '',
        appUrl: '',
        isActive: false,
        source: 'db',
      },
    };
  },
  async mounted() {
    await Promise.all([
      this.loadPackages(),
      this.loadSubscriptions(),
      this.loadPaymentSettings(),
      this.loadProdamusSettings(),
      this.loadSeoSettings(),
      this.loadSmtpSettings(),
    ]);
  },
  methods: {
    onProdamusCurrencyChange() {
      this.prodamusSettings.priceMap = this.prodamusSettings.currency === 'USD'
        ? { ...this.prodamusSettings.usdPrices } : {};
      this.prodamusSettings.isActive = false;
      this.prodamusSettings.available = false;
    },
    async refreshUsdPrices() {
      try {
        const data = await getProdamusSettings();
        this.prodamusSettings.usdPrices = data.usdPrices || {};
        this.prodamusSettings.available = data.available;
        if (this.prodamusSettings.currency === 'USD') {
          this.prodamusSettings.priceMap = { ...data.usdPrices };
        }
      } catch {
        this.prodamusError = this.$t('admin.usdRefreshError');
      }
    },
    applyProdamusSettings(data) {
      this.prodamusSettings = {
        ...data, priceMap: Object.fromEntries(
          Object.entries(data.priceMap || {}).map(([id, value]) => [id, String(value)])
        ),
      };
      this.savedProdamusWebhook = data.webhookUrl || '';
      this.prodamusSecret = '';
    },
    prodamusProductLabel(id) {
      if (id === 'base') return this.$t('subscription.base');
      if (id === 'premium') return this.$t('subscription.premium');
      const pkg = this.packages.find(item => item.package_id === id.slice(8));
      return pkg?.name || id;
    },
    async loadProdamusSettings() {
      try {
        this.prodamusLoading = true;
        this.prodamusError = null;
        this.applyProdamusSettings(await getProdamusSettings());
      } catch (error) {
        this.prodamusError = error.response?.data?.error || this.$t('admin.prodamusLoadError');
      } finally {
        this.prodamusLoading = false;
      }
    },
    async saveProdamus() {
      try {
        this.savingProdamus = true;
        this.prodamusError = null;
        this.prodamusSuccess = null;
        const { pageUrl, webhookUrl, currency, sys, priceMap, isActive,
          worldPaymentsEnabled, webhookReady } = this.prodamusSettings;
        const data = await saveProdamusSettings({
          pageUrl, webhookUrl, currency, sys, priceMap,
          isActive, worldPaymentsEnabled, webhookReady,
          ...(this.prodamusSecret ? { secretKey: this.prodamusSecret } : {}),
        });
        this.applyProdamusSettings(data);
        this.prodamusSuccess = this.$t('admin.prodamusSaved');
      } catch (error) {
        this.prodamusError = error.response?.data?.error || this.$t('admin.prodamusSaveError');
      } finally {
        this.savingProdamus = false;
      }
    },
    async copyProdamusWebhook() {
      try {
        await navigator.clipboard.writeText(this.savedProdamusWebhook);
        this.prodamusSuccess = this.$t('admin.prodamusCopied');
      } catch {
        this.prodamusError = this.$t('admin.prodamusCopyError');
      }
    },
    async loadPackages() {
      try {
        this.loading = true;
        this.error = null;
        const response = await axios.get('/credits/packages/all');
        this.packages = response.data;
      } catch (err) {
        console.error('Error loading packages:', err);
        this.error = this.$t('legacy.Не удалось загрузить пакеты кредитов');
      } finally {
        this.loading = false;
      }
    },
    async loadSubscriptions() {
      try {
        this.subscriptionsLoading = true;
        this.subscriptionError = null;
        const response = await axios.get('/credits/subscription-settings');
        this.subscriptions = response.data;
      } catch (err) {
        console.error('Error loading subscriptions:', err);
        this.subscriptionError = this.$t('legacy.Не удалось загрузить настройки тарифов');
      } finally {
        this.subscriptionsLoading = false;
      }
    },
    markChanged(id) {
      if (!this.changedPackages.includes(id)) {
        this.changedPackages.push(id);
      }
    },
    markSubscriptionChanged(id) {
      if (!this.changedSubscriptions.includes(id)) {
        this.changedSubscriptions.push(id);
      }
    },
    async savePackage(pkg) {
      try {
        this.saving = pkg.id;
        this.error = null;
        const response = await axios.put(`/credits/packages/${pkg.id}`, {
          price: pkg.price,
          price_usd: pkg.price_usd ?? null,
          is_active: pkg.is_active,
          credits: pkg.credits
        });
        pkg.price_usd = response.data.price_usd;
        this.changedPackages = this.changedPackages.filter(id => id !== pkg.id);
        await this.refreshUsdPrices();
      } catch (err) {
        console.error('Error saving package:', err);
        this.error = err.response?.data?.error || this.$t('legacy.Не удалось сохранить изменения');
      } finally {
        this.saving = null;
      }
    },
    async saveSubscription(sub) {
      try {
        this.savingSubscription = sub.id;
        this.subscriptionError = null;
        const response = await axios.put(`/credits/subscription-settings/${sub.id}`, {
          price: sub.price,
          price_usd: sub.price_usd ?? null,
          ai_credits: sub.ai_credits,
          is_active: sub.is_active
        });
        sub.price_usd = response.data.price_usd;
        this.changedSubscriptions = this.changedSubscriptions.filter(id => id !== sub.id);
        await this.refreshUsdPrices();
      } catch (err) {
        console.error('Error saving subscription:', err);
        this.subscriptionError = err.response?.data?.error || this.$t('legacy.Не удалось сохранить изменения');
      } finally {
        this.savingSubscription = null;
      }
    },
    async clearCache() {
      try {
        this.clearingCache = true;
        this.cacheSuccess = null;
        await axios.post('/admin/clear-cache');
        this.cacheSuccess = this.$t('legacy.Кэш успешно очищен. Клиенты получат свежие данные при следующем запросе.');
        setTimeout(() => {
          this.cacheSuccess = null;
        }, 5000);
      } catch (err) {
        console.error('Error clearing cache:', err);
        this.error = this.$t('legacy.Не удалось очистить кэш');
      } finally {
        this.clearingCache = false;
      }
    },
    async loadPaymentSettings() {
      try {
        this.paymentLoading = true;
        this.paymentError = null;
        const data = await getPaymentSettings();
        this.paymentSettings = {
          shopId: data.shopId || '',
          secretKey: data.secretKey || '',
          isActive: data.isActive || false,
          enableRecurringPayments: data.enableRecurringPayments || false,
        };
      } catch (err) {
        console.error('Error loading payment settings:', err);
        this.paymentError = this.$t('legacy.Не удалось загрузить настройки платежей');
      } finally {
        this.paymentLoading = false;
      }
    },
    async savePaymentSettings() {
      try {
        this.savingPayment = true;
        this.paymentError = null;
        this.paymentSuccess = null;
        
        await savePaymentSettingsApi({
          shopId: this.paymentSettings.shopId,
          secretKey: this.paymentSettings.secretKey || undefined,
          isActive: this.paymentSettings.isActive,
          enableRecurringPayments: this.paymentSettings.enableRecurringPayments,
        });
        
        this.paymentSuccess = this.$t('legacy.Настройки ЮКасса сохранены');
        
        setTimeout(() => {
          this.paymentSuccess = null;
        }, 5000);
      } catch (err) {
        console.error('Error saving payment settings:', err);
        this.paymentError = this.$t('legacy.Не удалось сохранить настройки');
      } finally {
        this.savingPayment = false;
      }
    },
    async loadSeoSettings() {
      try {
        this.seoLoading = true;
        this.seoError = null;
        const data = await getHomepageSettings();
        if (data) {
          this.seoSettings = {
            meta_title: data.meta_title || '',
            meta_description: data.meta_description || '',
            meta_keywords: data.meta_keywords || '',
            og_title: data.og_title || '',
            og_description: data.og_description || '',
            og_image: data.og_image || '',
            og_site_name: data.og_site_name || '',
            twitter_card: data.twitter_card || 'summary_large_image',
            twitter_title: data.twitter_title || '',
            twitter_description: data.twitter_description || ''
          };
        }
      } catch (err) {
        console.error('Error loading SEO settings:', err);
        this.seoError = this.$t('legacy.Не удалось загрузить SEO настройки');
      } finally {
        this.seoLoading = false;
      }
    },
    async saveSeoSettings() {
      try {
        this.savingSeo = true;
        this.seoError = null;
        this.seoSuccess = null;
        
        await updateHomepageSettings(this.seoSettings);
        
        this.seoSuccess = this.$t('legacy.SEO настройки сохранены');
        
        setTimeout(() => {
          this.seoSuccess = null;
        }, 5000);
      } catch (err) {
        console.error('Error saving SEO settings:', err);
        this.seoError = this.$t('Не удалось сохранить SEO настройки');
      } finally {
        this.savingSeo = false;
      }
    },
    async handleOgImageUpload(event) {
      const file = event.target.files[0];
      if (!file) return;
      
      try {
        this.uploadingOgImage = true;
        const result = await uploadOgImage(file);
        this.seoSettings.og_image = result.imageUrl;
        this.seoSettings.twitter_image = result.imageUrl;
      } catch (err) {
        console.error('Error uploading OG image:', err);
        this.seoError = this.$t('legacy.Не удалось загрузить изображение');
      } finally {
        this.uploadingOgImage = false;
      }
    },
    removeOgImage() {
      this.seoSettings.og_image = '';
      this.seoSettings.twitter_image = '';
    },
    async loadSmtpSettings() {
      try {
        this.smtpLoading = true;
        this.smtpError = null;
        const data = await smtpApi.getSettings();
        this.smtpSettings = {
          host: data.host || '',
          port: data.port || 465,
          username: data.username || '',
          password: data.password || '',
          fromEmail: data.fromEmail || '',
          appUrl: data.appUrl || '',
          isActive: data.isActive || false,
          source: data.source || 'db',
        };
      } catch (err) {
        console.error('Error loading SMTP settings:', err);
        this.smtpError = this.$t('legacy.Не удалось загрузить настройки SMTP');
      } finally {
        this.smtpLoading = false;
      }
    },
    async saveSmtpSettings() {
      try {
        this.savingSmtp = true;
        this.smtpError = null;
        this.smtpSuccess = null;
        this.smtpTestResult = null;

        await smtpApi.saveSettings({
          host: this.smtpSettings.host,
          port: this.smtpSettings.port,
          username: this.smtpSettings.username,
          password: this.smtpSettings.password,
          fromEmail: this.smtpSettings.fromEmail,
          appUrl: this.smtpSettings.appUrl,
          isActive: this.smtpSettings.isActive,
        });

        this.smtpSuccess = this.$t('legacy.Настройки Email сохранены');
        await this.loadSmtpSettings();

        setTimeout(() => {
          this.smtpSuccess = null;
        }, 5000);
      } catch (err) {
        console.error('Error saving SMTP settings:', err);
        this.smtpError = this.$t('legacy.Не удалось сохранить настройки');
      } finally {
        this.savingSmtp = false;
      }
    },
    async testSmtpConnection() {
      try {
        this.testingSmtp = true;
        this.smtpTestResult = null;
        this.smtpError = null;

        const result = await smtpApi.testConnection();
        this.smtpTestResult = {
          success: result.success,
          message: result.success ? result.message : (result.error || this.$t('legacy.Не удалось подключиться')),
        };

        setTimeout(() => {
          this.smtpTestResult = null;
        }, 12000);
      } catch (err) {
        console.error('Error testing SMTP connection:', err);
        this.smtpTestResult = {
          success: false,
          message: this.$t('legacy.Ошибка при проверке соединения'),
        };
      } finally {
        this.testingSmtp = false;
      }
    },
  }
};
</script>

<style scoped lang="scss">
.admin-settings {
  h2 {
    margin: 0 0 30px 0;
    font-size: 24px;
    color: #333;
  }
}

.input-with-toggle {
  position: relative;
  display: flex;
  align-items: center;

  .form-input {
    flex: 1;
    padding-right: 42px;
  }

  .toggle-password-btn {
    position: absolute;
    right: 8px;
    background: none;
    border: none;
    cursor: pointer;
    font-size: 16px;
    padding: 4px;
    line-height: 1;
    opacity: 0.6;
    transition: opacity 0.2s;

    &:hover {
      opacity: 1;
    }
  }
}

.settings-section {
  background: #fff;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  
  h3 {
    margin: 0 0 8px 0;
    font-size: 18px;
    color: #333;
  }
  
  .section-description {
    color: #666;
    margin: 0 0 24px 0;
    font-size: 14px;
  }
}

.loading {
  text-align: center;
  padding: 40px;
  color: #666;
}

.packages-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.package-card {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
  
  .package-info {
    display: flex;
    flex-direction: column;
    gap: 4px;
    
    .package-name {
      font-weight: 600;
      color: #333;
    }
    
    .package-credits {
      font-size: 14px;
      color: #666;
    }
  }
  
  .package-controls {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    align-items: center;
    gap: 20px;
    
    .price-input-group {
      display: flex;
      align-items: center;
      gap: 8px;
      
      label {
        font-size: 14px;
        color: #666;
      }
      
      .price-input {
        width: 100px;
        padding: 8px 12px;
        border: 1px solid #ddd;
        border-radius: 4px;
        font-size: 14px;
        
        &:focus {
          outline: none;
          border-color: #496782;
        }
      }
    }
    
    .active-toggle {
      .toggle-label {
        display: flex;
        align-items: center;
        gap: 6px;
        cursor: pointer;
        font-size: 14px;
        color: #666;
        
        input[type="checkbox"] {
          width: 18px;
          height: 18px;
          cursor: pointer;
        }
      }
    }
    
    .btn-save {
      padding: 8px 16px;
      background: #496782;
      color: #fff;
      border: none;
      border-radius: 4px;
      font-size: 14px;
      cursor: pointer;
      transition: background 0.2s;
      
      &:hover:not(:disabled) {
        background: #3a5569;
      }
      
      &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }
    }
  }
}

.error-message {
  margin-top: 16px;
  padding: 12px;
  background: #fee;
  color: #c33;
  border-radius: 4px;
  font-size: 14px;
}

.success-message {
  margin-top: 16px;
  padding: 12px;
  background: #d4edda;
  color: #155724;
  border-radius: 4px;
  font-size: 14px;
}

.cache-controls {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  
  .btn-clear-cache {
    padding: 12px 24px;
    background: #dc3545;
    color: #fff;
    border: none;
    border-radius: 4px;
    font-size: 14px;
    cursor: pointer;
    transition: background 0.2s;
    
    &:hover:not(:disabled) {
      background: #c82333;
    }
    
    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }
  
  .cache-hint {
    margin: 0;
    font-size: 13px;
    color: #666;
  }
}

.settings-section + .settings-section {
  margin-top: 24px;
}

.info-message {
  margin-bottom: 16px;
  padding: 12px;
  background: #e0f4fc;
  color: #26374e;
  border-radius: 4px;
  font-size: 13px;
  border-left: 3px solid #496782;
}

.btn-secondary {
  padding: 8px 16px;
  background: transparent;
  color: #496782;
  border: 1px solid #496782;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: #496782;
    color: #fff;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.payment-settings-form {
  .form-group {
    margin-bottom: 20px;
    
    label {
      display: block;
      margin-bottom: 8px;
      font-size: 14px;
      font-weight: 500;
      color: #333;
    }
    
    .form-input {
      width: 100%;
      max-width: 400px;
      padding: 10px 12px;
      border: 1px solid #ddd;
      border-radius: 4px;
      font-size: 14px;
      
      &:focus {
        outline: none;
        border-color: #496782;
      }
    }
    
    .field-hint {
      margin-top: 6px;
      font-size: 12px;
      color: #888;
    }
    
    .toggle-label {
      display: flex;
      align-items: center;
      gap: 8px;
      cursor: pointer;
      font-size: 14px;
      
      input[type="checkbox"] {
        width: 18px;
        height: 18px;
        cursor: pointer;
      }
    }
  }
  
  .form-actions {
    margin-top: 24px;
    
    .btn-save {
      padding: 10px 24px;
      background: #496782;
      color: #fff;
      border: none;
      border-radius: 4px;
      font-size: 14px;
      cursor: pointer;
      transition: background 0.2s;
      
      &:hover:not(:disabled) {
        background: #3a5569;
      }
      
      &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }
    }
  }
  
  .webhook-info {
    margin-top: 32px;
    padding: 16px;
    background: #f0f7ff;
    border: 1px solid #c5ddf8;
    border-radius: 8px;
    
    h4 {
      margin: 0 0 12px 0;
      font-size: 14px;
      color: #1a5490;
    }
    
    p {
      margin: 0 0 8px 0;
      font-size: 13px;
      color: #333;
    }
    
    .webhook-url {
      display: block;
      margin: 12px 0;
      padding: 10px 14px;
      background: #fff;
      border: 1px solid #ddd;
      border-radius: 4px;
      font-family: monospace;
      font-size: 13px;
      color: #333;
      word-break: break-all;
    }
    
    .webhook-hint {
      margin: 0;
      font-size: 12px;
      color: #666;
      
      strong {
        color: #1a5490;
      }
    }
  }
}

.seo-settings-form {
  h4 {
    margin: 24px 0 16px 0;
    font-size: 16px;
    color: #333;
    padding-bottom: 8px;
    border-bottom: 1px solid #e9ecef;
    
    &:first-child {
      margin-top: 0;
    }
  }
  
  .form-group {
    margin-bottom: 20px;
    
    label {
      display: block;
      margin-bottom: 8px;
      font-size: 14px;
      font-weight: 500;
      color: #333;
    }
    
    .form-input {
      width: 100%;
      max-width: 600px;
      padding: 10px 12px;
      border: 1px solid #ddd;
      border-radius: 4px;
      font-size: 14px;
      
      &:focus {
        outline: none;
        border-color: #496782;
      }
    }
    
    .form-textarea {
      resize: vertical;
      min-height: 60px;
    }
  }
  
  .og-image-upload {
    max-width: 600px;
  }
  
  .og-image-preview {
    position: relative;
    display: inline-block;
    
    img {
      max-width: 300px;
      max-height: 200px;
      border-radius: 4px;
      border: 1px solid #ddd;
    }
    
    .btn-remove-image {
      position: absolute;
      top: 8px;
      right: 8px;
      padding: 4px 8px;
      background: rgba(220, 53, 69, 0.9);
      color: #fff;
      border: none;
      border-radius: 4px;
      font-size: 12px;
      cursor: pointer;
      
      &:hover {
        background: #dc3545;
      }
    }
  }
  
  .og-image-placeholder {
    .btn-upload {
      padding: 10px 20px;
      background: #6c757d;
      color: #fff;
      border: none;
      border-radius: 4px;
      font-size: 14px;
      cursor: pointer;
      
      &:hover:not(:disabled) {
        background: #5a6268;
      }
      
      &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }
    }
  }
  
  .form-actions {
    margin-top: 24px;
  }
}

@media (max-width: 768px) {
  .package-card {
    flex-direction: column;
    gap: 16px;
    align-items: flex-start;
    
    .package-controls {
      flex-wrap: wrap;
      width: 100%;
    }
  }
  
  .seo-settings-form {
    .form-input {
      max-width: 100% !important;
    }
    
    .og-image-preview img {
      max-width: 100%;
    }
  }
}
</style>
